import React, { useEffect, useState, useCallback } from 'react';
import { useClient } from 'shared/apollo';
import TrueImpactModal from 'shared/design-system/components/TrueImpactModal';
import { Typography, TextField, Button, MenuItem, CircularProgress } from '@mui/material';
import {
  Heading,
  RowH,
  ColH,
  Select,
  CloseIcon,
  CancelButton,
} from './styles';
import { useFormik } from 'formik';
import { updateUser, newUser, resendUserInvitation } from './api';
import PasswordInstructions, {
  getPasswordValidations,
} from 'shared/no-auth/components/AccountEditor/PasswordInstructions';
import styled from 'styled-components';
import { COLORS } from 'shared/design-system/settings/variables/Colors';

const options = [
  { label: 'Select', value: 'Select' },
  { label: 'He/him', value: 'He/him' },
  { label: 'She/her', value: 'She/her' },
  { label: 'They/them', value: 'They/them' },
  { label: 'Prefer not to share', value: 'Prefer not to share' },
  { label: 'Other', value: 'other' },
];

const StyledResendInvitation = styled.div`
  .resend-button {
    margin-bottom: 16px;
  }
  .resend-success-message {
    color: ${COLORS.success};
    margin-bottom: 16px;
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const UserModal: React.FC<any> = ({
  open,
  setOpen,
  user,
  handleClose,
  accountId,
}) => {
  const client = useClient();
  const isNew = !user?.dbId;

  const [resendCompleted, setResendCompleted] = useState<string | undefined>(
    undefined,
  );
  const [passwordValidations, setPasswordValidations] = useState<boolean[]>([]);
  const [error, setError] = useState<string>('');
  const [resendLoading, setResendLoading] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);

  const handleResendInvitation = useCallback(async () => {
    setError('');
    setResendCompleted(undefined);
    setResendLoading(true);
    try {
      const result = await resendUserInvitation(client, {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        accountId,
      });
      if (!result.success) {
        setError(result.error || 'Unable to resend the invitation');
        return;
      }
      setResendCompleted(`Invitation successfully resent to ${user.email}!`);
    } finally {
      setResendLoading(false);
    }
  }, [user, client, accountId]);

  const validateUser = values => {
    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    if (!values.email || !emailRegex.test(values.email)) {
      setError('Invalid email address');
      return;
    }
    if (!values.pronoun || values.pronoun === 'Select') {
      setError('Please select a pronoun');
      return;
    }
    if (!values.firstName || values.firstName.length < 2) {
      setError('First name must be at least 2 characters long');
      return;
    }
    if (!values.lastName || values.lastName.length < 2) {
      setError('Last name must be at least 2 characters long');
      return;
    }
    if (!values.password) {
      setError('Password is required');
      return;
    }
    if (!passwordValidations.every(item => item === true)) {
      setError('Passwords is not valid');
      return;
    }
    if (values.password !== values.repassword) {
      setError('Passwords do not match');
      return;
    }
    setError('');
    return true;
  };

  const { values, setFieldValue, handleSubmit } = useFormik({
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      email: '',
      firstName: user?.firstName,
      lastName: user?.lastName,
      pronoun: user?.pronoun || 'Select',
      title: user?.title,
      phone: user?.phone,
      password: '',
      repassword: '',
    },
    onSubmit: async value => {
      if (saveLoading) return;
      setSaveLoading(true);
      try {
        if (isNew) {
          if (!validateUser(value)) return;
          const resp = await newUser(client, {
            ...value,
            accountId,
            userType: 'admin',
          });
          if (!resp.success) {
            setError(resp.error || 'Unable to create the user');
            return;
          }
          handleClose({
            ...value,
            accountId,
            userType: 'admin',
            newUserId: resp.data?.userId,
          });
          return;
        }
        const { email, password, repassword, ...rest } = value;
        await updateUser(client, { ...rest, id: user.dbId });
        handleClose({ ...rest, id: user.dbId });
      } finally {
        setSaveLoading(false);
      }
    },
  });

  const handlePasswordChange = e => {
    const value = e.target.value;
    setFieldValue('password', value);
    setPasswordValidations(getPasswordValidations(value));
  };

  useEffect(() => {
    setFieldValue('email', '');
    setFieldValue('password', '');
    setFieldValue('repassword', '');
    setFieldValue('firstName', user?.firstName);
    setFieldValue('lastName', user?.lastName);
    setFieldValue('pronoun', user?.pronoun ?? 'Select');
    setFieldValue('title', user?.title);
    setFieldValue('phone', user?.phone);
  }, [user]);

  return (
    <TrueImpactModal
      open={open}
      hideHeader={true}
      hideFooter={true}
      onClose={() => setOpen(false)}
    >
      <Heading>
        <CloseIcon onClick={() => handleClose()} />
        <RowH>
          <ColH>
            <Typography variant="h2">
              {!isNew ? 'Edit' : 'Add new'} user
            </Typography>
          </ColH>
        </RowH>
        {!isNew && (
          <RowH>
            <ColH>
              <span>
                Email: {user?.email || '-'} (<span>ID: {user?.dbId}</span>)
              </span>
            </ColH>
          </RowH>
        )}
      </Heading>
      <ColH
        style={{
          paddingLeft: '25%',
          paddingRight: '25%',
          paddingTop: '10px',
          paddingBottom: '10px',
        }}
      >
        {isNew && (
          <RowH>
            <ColH style={{ marginRight: '10px' }}>
              <p>Email:</p>
              <TextField
                placeholder="Email"
                fullWidth
                values={values.email}
                onChange={e => setFieldValue('email', e.target.value)}
                defaultValue={values.email}
              />
            </ColH>
          </RowH>
        )}
        <RowH>
          <ColH style={{ marginRight: '10px' }}>
            <p>First Name:</p>
            <TextField
              placeholder="First Name"
              fullWidth
              values={values.firstName}
              onChange={e => setFieldValue('firstName', e.target.value)}
              defaultValue={values.firstName}
            />
          </ColH>
          <ColH style={{ marginRight: '10px' }}>
            <p>Last Name:</p>
            <TextField
              placeholder="Last Name"
              fullWidth
              values={values.lastName}
              onChange={e => setFieldValue('lastName', e.target.value)}
              defaultValue={values.lastName}
            />
          </ColH>
          <ColH>
            <p>Pronoun:</p>
            <Select
              value={values.pronoun}
              onChange={e => {
                setFieldValue('pronoun', e.target.value);
              }}
            >
              {options.map(opt => (
                <MenuItem value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </ColH>
        </RowH>
        <RowH>
          <ColH style={{ marginRight: '10px' }}>
            <p>Job title:</p>
            <TextField
              placeholder="Job title"
              fullWidth
              values={values.title}
              onChange={e => setFieldValue('title', e.target.value)}
              defaultValue={values.title}
            />
          </ColH>
          <ColH>
            <p>Phone:</p>
            <TextField
              placeholder="phone"
              fullWidth
              values={values.phone}
              onChange={e => setFieldValue('phone', e.target.value)}
              defaultValue={values.phone}
            />
          </ColH>
        </RowH>
        {isNew && (
          <RowH>
            <ColH style={{ marginRight: '10px' }}>
              <p>Password:</p>
              <TextField
                placeholder="Password"
                type="password"
                fullWidth
                values={values.password}
                onChange={handlePasswordChange}
                defaultValue={values.password}
              />
            </ColH>
            <ColH style={{ marginRight: '10px' }}>
              <p>Confirm Password:</p>
              <TextField
                placeholder="Confirm Password"
                type="password"
                fullWidth
                values={values.repassword}
                onChange={e => setFieldValue('repassword', e.target.value)}
                defaultValue={values.repassword}
              />
            </ColH>
          </RowH>
        )}
        {isNew && (
          <RowH>
            <PasswordInstructions
              passwordValidations={passwordValidations}
              password={values.password}
              confirmPassword={values.repassword}
            />
          </RowH>
        )}
        {error && (
          <RowH style={{ textAlign: 'center', display: 'block' }}>
            <Typography style={{ color: '#ff7272' }} variant="body2">
              {error}
            </Typography>
          </RowH>
        )}

        <StyledResendInvitation>
          {!isNew && (
            <CancelButton
              onClick={() => !resendLoading && handleResendInvitation()}
              className="resend-button"
            >
              {resendLoading ? (
                <CircularProgress size={16} />
              ) : (
                'Resend Invitation'
              )}
            </CancelButton>
          )}
          {resendCompleted && (
            <div className="resend-success-message">{resendCompleted}</div>
          )}
        </StyledResendInvitation>
        <RowH style={{ alignItems: 'center' }}>
          <ColH>
            <Button fullwidth onClick={handleSubmit}>
              Save
            </Button>
          </ColH>
          <ColH style={{ paddingLeft: '3px' }}>
            <CancelButton onClick={() => handleClose()}>Cancel</CancelButton>
          </ColH>
          {saveLoading && (
            <ColH style={{ paddingLeft: '8px' }}>
              <CircularProgress size={18} />
            </ColH>
          )}
        </RowH>
      </ColH>
    </TrueImpactModal>
  );
};

export default UserModal;
